/**
 * The category taxonomies. Ported from `BudgetItem.swift`.
 *
 * These are hardcoded in both clients rather than stored server-side, matching
 * the iOS app. Adding a category means shipping both apps, which is the reason
 * to move this into the Settings table eventually.
 */

import { roundCents } from './money';
import { getPayDays, payIndexOfPeriod, PaycheckPeriod, periodCoversDay } from './paycheck';
import { FundingSchedule } from './types';

export const ENVELOPE_CATEGORIES = [
  'Food & Dining',
  'Transportation',
  'Personal & Family',
  'Entertainment & Recreation',
  'Education & Children',
  'Pets & Animals',
  'Miscellaneous',
  'Savings & Emergency',
] as const;

export const BILL_CATEGORIES = [
  'Housing',
  'Utilities',
  'Insurance',
  'Healthcare',
  'Services',
  'Other',
] as const;

export type EnvelopeCategory = (typeof ENVELOPE_CATEGORIES)[number];
export type BillCategory = (typeof BILL_CATEGORIES)[number];

export function categoriesFor(kind: 'envelope' | 'bill'): readonly string[] {
  return kind === 'envelope' ? ENVELOPE_CATEGORIES : BILL_CATEGORIES;
}

/**
 * The palette a budget item's `color` may name. The iOS app maps these to
 * SwiftUI colors; the web app maps them to CSS custom properties.
 */
export const ITEM_COLORS = [
  'green', 'blue', 'purple', 'orange', 'pink',
  'yellow', 'brown', 'gray', 'red', 'cyan', 'indigo',
] as const;

export type ItemColor = (typeof ITEM_COLORS)[number];

export function normalizeItemColor(color: string): ItemColor {
  const lower = color.toLowerCase();
  if (lower === 'grey') return 'gray';
  return (ITEM_COLORS as readonly string[]).includes(lower) ? (lower as ItemColor) : 'gray';
}

export function ordinalDay(day: number): string {
  const suffix =
    day === 1 || day === 21 || day === 31
      ? 'st'
      : day === 2 || day === 22
        ? 'nd'
        : day === 3 || day === 23
          ? 'rd'
          : 'th';
  return `${day}${suffix}`;
}

/** "Due on the 15th" */
export function dueDateDescription(dueDay: number | null | undefined): string | null {
  if (dueDay == null) return null;
  return `Due on the ${ordinalDay(dueDay)}`;
}

/** "the 1st and the 16th", or "the 1st, the 8th, and the 15th". */
export function listedPayDays(days: readonly number[] = getPayDays()): string {
  const names = days.map(ordinalDay);
  if (names.length <= 1) return `the ${names[0] ?? '1st'}`;
  if (names.length === 2) return `the ${names[0]} and the ${names[1]}`;
  return `the ${names.slice(0, -1).join(', the ')}, and the ${names[names.length - 1]}`;
}

/** How many paychecks land in a month. Follows the configured pay days. */
export function paychecksPerMonth(schedule?: FundingSchedule | null): number {
  const count = Math.max(getPayDays().length, 1);
  return schedule == null || schedule === 'everyPaycheck' ? count : 1;
}

/**
 * What one round of funding adds up to over a month.
 *
 * Two rounds for something split across both paychecks; one for something whose
 * whole cost comes out of a single paycheck.
 */
export function monthlyAmount(
  perPaycheckAmount: number,
  schedule?: FundingSchedule | null
): number {
  return roundCents(perPaycheckAmount * paychecksPerMonth(schedule));
}

/** A budget item's cost over a month, whichever way it is funded. */
export function monthlyAmountOf(item: {
  perPaycheckAmount: number;
  fundingSchedule?: FundingSchedule | null;
}): number {
  return monthlyAmount(item.perPaycheckAmount, item.fundingSchedule);
}

/** Whether the paycheck covering `period` funds an item on this schedule. */
export function scheduleFundsPeriod(
  schedule: FundingSchedule | null | undefined,
  period: PaycheckPeriod,
  fundingDay?: number | null
): boolean {
  if (schedule == null || schedule === 'everyPaycheck') return true;
  if (schedule === 'onDay') return periodCoversDay(period, fundingDay ?? 1);
  if (schedule === 'firstPaycheck') return payIndexOfPeriod(period) === 0;
  if (schedule === 'secondPaycheck') return payIndexOfPeriod(period) === 1;
  return false;
}

/**
 * The other direction: a bill's whole monthly cost, split across the paychecks
 * that fund it.
 *
 * A budget item stores only what each paycheck sets aside, so entering a $1,995
 * rent means typing $997.50 — arithmetic done in a separate calculator, wrong
 * the moment the rent changes. An odd-cent total cannot halve exactly; the
 * rounded half is a hair generous, so `monthlyAmount` of the result can come to
 * a penny more than what was typed. Say so rather than hide it.
 */
export function perPaycheckFromMonthly(
  monthlyTotal: number,
  schedule?: FundingSchedule | null
): number {
  return roundCents(monthlyTotal / paychecksPerMonth(schedule));
}
